'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { Control, UseFormHandleSubmit, useWatch } from 'react-hook-form'

import { SplitsLink } from '~/components/Link'
import { Button, Message, TextInput } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import {
  DOCS_URL,
  getSplitsConnectInstallAttribution,
} from '~/constants/externalUrls'
import { DISMISSED_SPLITS_CONNECT_INTERSTITIAL_KEY } from '~/constants/localStorage'
import { useWalletConnectContext } from '~/context/WalletConnectProvider'
import { useCanInstallSplitsConnect } from '~/hooks/useCanInstallSplitsConnect'
import { useIsSplitsConnectInstalled } from '~/hooks/useIsSplitsConnectInstalled'
import {
  updateSyncedLocalStorage,
  useSyncedLocalStorage,
} from '~/hooks/useSyncedLocalStorage'
import { sendTrackingData } from '~/utils/tracking'

const SPLITS_CONNECT_DOCS_URL = `${DOCS_URL}/introduction/extension`

const INSTALL_ATTRIBUTION = getSplitsConnectInstallAttribution(
  'walletConnectDialog',
)

export type WalletConnectFormValues = {
  uri: string
}

type Props = {
  control: Control<WalletConnectFormValues>
  handleSubmit: UseFormHandleSubmit<WalletConnectFormValues>
  open: boolean
}

export const WalletConnectPairingDialog = ({
  control,
  handleSubmit,
  open,
}: Props) => {
  const isSplitsConnectInstalled = useIsSplitsConnectInstalled()
  const canInstallSplitsConnect = useCanInstallSplitsConnect()
  const dismissedSplitsConnectInterstitial = useSyncedLocalStorage(
    DISMISSED_SPLITS_CONNECT_INTERSTITIAL_KEY,
  )
  const showSplitsConnectInterstitial =
    canInstallSplitsConnect && !dismissedSplitsConnectInterstitial
  const trackedInterstitialViewRef = useRef(false)

  // eslint-disable-next-line local/noUseEffect -- dialog-open analytics synchronizes controlled state with an external service
  useEffect(() => {
    if (!open) {
      trackedInterstitialViewRef.current = false
      return
    }
    if (showSplitsConnectInterstitial && !trackedInterstitialViewRef.current) {
      trackedInterstitialViewRef.current = true
      sendTrackingData('viewedSplitsConnectInstallInterstitial')
    }
  }, [open, showSplitsConnectInterstitial])

  if (showSplitsConnectInterstitial) {
    return <SplitsConnectInterstitial />
  }

  return (
    <WalletConnectForm
      control={control}
      handleSubmit={handleSubmit}
      isSplitsConnectInstalled={isSplitsConnectInstalled}
      canInstallSplitsConnect={canInstallSplitsConnect}
    />
  )
}

const SplitsConnectInterstitial = () => {
  const [installClicked, setInstallClicked] = useState(false)

  return (
    <SplitsDialogContent
      id="smartAccountConnectWallet"
      title="Connect to apps"
      variant="cta"
      actionButton={{
        element: (
          <InstallExtensionButton onClick={() => setInstallClicked(true)} />
        ),
      }}
      cancelButton={{
        text: 'Use WalletConnect',
        eventName: 'clickedUseWalletConnect',
        onClick: () =>
          updateSyncedLocalStorage(
            DISMISSED_SPLITS_CONNECT_INTERSTITIAL_KEY,
            () => 'true',
          ),
      }}
    >
      <p>
        {installClicked
          ? 'Installed? Open or refresh the app you want to connect to, then select Splits from the list of wallets.'
          : 'Splits Connect is a browser extension to use your Splits accounts with any app. Once installed, Splits will appear in the list of wallets when connecting to other apps.'}{' '}
        <SplitsLink path={SPLITS_CONNECT_DOCS_URL} target="tab" underline>
          Learn more
        </SplitsLink>
      </p>
    </SplitsDialogContent>
  )
}

// The dialog clones a ⌘+↵ badge onto element-style action buttons, but an
// external link can't honor it. Wrapping the button keeps that prop off it.
const InstallExtensionButton = ({
  fullWidth,
  onClick,
}: {
  fullWidth?: boolean
  onClick: () => void
}) => (
  <Button
    fullWidth={fullWidth}
    href={{ path: INSTALL_ATTRIBUTION.url, target: 'tab' }}
    eventName="clickedInstallSplitsConnect"
    eventProperties={INSTALL_ATTRIBUTION.eventProperties}
    onClick={onClick}
  >
    Install extension
  </Button>
)

const WalletConnectForm = ({
  control,
  handleSubmit,
  isSplitsConnectInstalled,
  canInstallSplitsConnect,
}: {
  control: Control<WalletConnectFormValues>
  handleSubmit: UseFormHandleSubmit<WalletConnectFormValues>
  isSplitsConnectInstalled: boolean
  canInstallSplitsConnect: boolean
}) => {
  const { isPairing, pairToDapp } = useWalletConnectContext()
  const value = useWatch({ control, name: 'uri' })
  const onSubmit = useCallback(
    async (data: WalletConnectFormValues) => {
      await pairToDapp(data.uri)
    },
    [pairToDapp],
  )

  return (
    <SplitsDialogContent
      id="smartAccountConnectWallet"
      title="Connect to apps"
      variant="cta"
      isLoading={isPairing}
      actionButton={{
        text: 'Connect',
        isDisabled: !value,
        onClick: handleSubmit(onSubmit),
      }}
    >
      <SplitsConnectMessage
        isSplitsConnectInstalled={isSplitsConnectInstalled}
        canInstallSplitsConnect={canInstallSplitsConnect}
      />
      <p>
        Copy the WalletConnect link from the app you wish to connect and paste
        it below.
      </p>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <TextInput
            label="WalletConnect link"
            control={control}
            inputName="uri"
            placeholder="wc:"
            autoFocus
          />
        </div>
      </form>
    </SplitsDialogContent>
  )
}

const SplitsConnectMessage = ({
  isSplitsConnectInstalled,
  canInstallSplitsConnect,
}: {
  isSplitsConnectInstalled: boolean
  canInstallSplitsConnect: boolean
}) => {
  if (isSplitsConnectInstalled) {
    return (
      <Message variant="info">
        <span>
          You have the Splits extension installed. Select Splits in the
          app&apos;s wallet list to connect directly to your accounts.{' '}
          <SplitsLink path={SPLITS_CONNECT_DOCS_URL} target="tab" underline>
            Learn more
          </SplitsLink>
        </span>
      </Message>
    )
  }

  if (!canInstallSplitsConnect) {
    return (
      <Message variant="info">
        <span>
          The Splits Chrome extension can connect your accounts directly to
          other apps.{' '}
          <SplitsLink path={SPLITS_CONNECT_DOCS_URL} target="tab" underline>
            Learn more
          </SplitsLink>
        </span>
      </Message>
    )
  }

  return (
    <Message variant="info">
      <span>
        Install the{' '}
        <SplitsLink
          path={INSTALL_ATTRIBUTION.url}
          target="tab"
          underline
          eventName="clickedInstallSplitsConnect"
          eventProperties={INSTALL_ATTRIBUTION.eventProperties}
        >
          Splits extension
        </SplitsLink>{' '}
        to connect your accounts directly to other apps.
      </span>
    </Message>
  )
}
